// Web Push: the contract between a browser's PushSubscription, the server that stores it, and the
// service worker that renders the notification.
//
// Scope note: push reaches a device that has the app CLOSED. That makes it the loudest channel in
// the product, so the kinds below are deliberately few and each one is something the recipient
// would want to be interrupted for.
import { z } from 'zod';

/** What a notification is about. Drives the icon, the grouping tag, and where a tap lands. */
export const PUSH_KINDS = [
  'chat', // the lab replied
  'approval', // the doctor's sign-off is blocking the case
  'ready', // finished / dispatched
  'status', // the case reached a new public milestone
  'registration', // a clinic signed up and is waiting for the lab to activate it
  'tracking', // a rep on duty has gone quiet - open the app to resume the route
  'test', // "send me a test" from the settings screen
] as const;
export type PushKind = (typeof PUSH_KINDS)[number];

export const PUSH_KIND_LABELS: Record<PushKind, string> = {
  chat: 'Messages from the lab',
  approval: 'Approvals waiting on you',
  ready: 'Cases ready or dispatched',
  status: 'Case progress updates',
  registration: 'New clinic registrations',
  tracking: 'Tracking paused while on duty',
  test: 'Test notification',
};

/**
 * Notifications that collapse onto one another. A second chat message replaces the first in the
 * tray rather than stacking five copies — the doctor wants to know the lab wrote, not how often.
 */
export const pushTag = (kind: PushKind, caseId?: string | null): string =>
  caseId ? `${kind}:${caseId}` : kind;

/** The payload the service worker receives. Keep it small — some push services cap at ~4KB. */
export interface PushPayload {
  kind: PushKind;
  title: string;
  body: string;
  /** Where a tap should land, e.g. /portal/cases/<id>. */
  url: string;
  tag: string;
  /** Notifications the recipient should not have to dismiss silently. */
  requireInteraction?: boolean;
}

/** Browser → server. Mirrors PushSubscription.toJSON(). */
export const pushSubscribeSchema = z.object({
  endpoint: z.string().url().max(2000),
  keys: z.object({
    p256dh: z.string().min(1).max(255),
    auth: z.string().min(1).max(255),
  }),
  /** Helps a user recognise which device a subscription belongs to. */
  userAgent: z.string().max(300).optional(),
});
export type PushSubscribeInput = z.infer<typeof pushSubscribeSchema>;

export const pushUnsubscribeSchema = z.object({ endpoint: z.string().url().max(2000) });

/** Server → browser at startup. `null` key means push is not configured on this deployment. */
export interface PushConfig {
  publicKey: string | null;
  /** How many devices this account currently has registered. */
  devices: number;
}
